// Transaction designer → List View Settings → Tabs (`list_view.tabs`).
//
// Two ways to make tabs, the same two the engine reads (list_tabs.py):
//   • Split by one field — pick a main-table field with a fixed list of
//     choices (drop-down / radio); each choice becomes a tab, named and ordered
//     as in that list. Choices can be hidden and reordered.
//   • Custom tabs — each tab its own label and conditions (ANDed), with the
//     same operators and value tokens the search scopes use. A Vision screen's
//     record states (`list_view.vision_states`) can seed them.
// Plus: "Show an All tab", "Show the number of records on each tab", and the
// tab opened first. Nothing here is a literal: limits and operators come from
// listScreenConfig.json, which the engine reads too.

import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'
import type { ColumnDef, EnforcedFilterOp, ListTabCondition, ListTabItem, ListTabsConfig, SqlModel } from './types'
import { filterOpsForColumn, acceptsCalendarDefault } from './types'
import {
  ALL_TAB_ID, TAB_LIMITS, TAB_OPS, listTabsOf, mainTableColumns, picklistOf, columnDbName,
} from '@twasta/page-runtime/txn/list'
import DefaultValuePicker, { type PickerGroup } from '@/components/designers/DefaultValuePicker'
import type { ProjectVariable } from '@/api/projectVariables'

const OP_LABELS: Record<string, string> = {
  between: 'from–to', eq: '=', neq: '≠', in: 'in', gt: '>', gte: '≥', lt: '<', lte: '≤',
  contains: 'contains', starts_with: 'starts with', is_empty: 'is empty', is_not_empty: 'is not empty',
}

/** Who-is-signed-in values a tab may compare with (resolved by the engine). */
const IDENTITY_TOKENS: readonly PickerGroup[] = [{
  title: 'The signed-in person',
  entries: [
    { token: ':audit_add_user', label: 'Their user id as the audit columns store it (records they created)' },
    { token: ':user_login', label: 'Their sign-in (email, else user id)' },
    { token: ':user_email', label: 'Their email' },
    { token: ':user_id', label: 'Their user id' },
  ],
}]

const RECORD_STATE_LABELS: Record<string, string> = {
  confirm: 'Confirmed', cancel: 'Cancelled', close: 'Closed',
}

interface Styles {
  fieldLabelStyle: React.CSSProperties
  inputStyle: React.CSSProperties
  checkboxRowStyle: React.CSSProperties
  tokenWarnStyle: React.CSSProperties
}

export default function ListTabsEditor({
  form, projectVars, onChange, styles,
}: {
  form: SqlModel
  projectVars: ProjectVariable[]
  /** The new `list_view.tabs` (undefined removes it). */
  onChange: (tabs: ListTabsConfig | undefined) => void
  styles: Styles
}) {
  const { fieldLabelStyle, inputStyle, checkboxRowStyle, tokenWarnStyle } = styles
  const tb: ListTabsConfig | undefined = form.list_view?.tabs
  const mainCols = mainTableColumns(form)
  const picklistCols = mainCols.filter((c) => picklistOf(c).length > 0)
  const mode: 'off' | 'field' | 'custom' = !tb ? 'off' : (tb.items?.length ? 'custom' : 'field')
  const effective = listTabsOf(form)
  const colOf = (key: string): ColumnDef | undefined =>
    mainCols.find((c) => columnDbName(c) === String(key || '').toLowerCase())
  const patch = (p: Partial<ListTabsConfig>) => onChange({ ...(tb || {}), ...p })

  const startField = () => {
    const first = picklistCols[0]
    onChange({ show_all: true, counts: true, default: ALL_TAB_ID,
      column: first ? (first.db_name || first.name) : '' })
  }
  const startCustom = (items?: ListTabItem[]) => {
    const first = mainCols[0]
    onChange({
      show_all: tb?.show_all ?? true, counts: tb?.counts ?? true, default: ALL_TAB_ID,
      items: items || [{ id: 'tab1', label: 'Tab 1',
        when: [{ column: first ? (first.db_name || first.name) : '', op: 'eq', value: '' }] }],
    })
  }

  // ── Split by one field ──────────────────────────────────────────────
  const fieldCol = mode === 'field' ? colOf(tb?.column || '') : undefined
  const choices = picklistOf(fieldCol)
  const shownValues: string[] = (tb?.values && tb.values.length)
    ? tb.values.filter((v) => choices.some((c) => c.data === v))
    : choices.map((c) => c.data)
  const setValues = (vals: string[]) => {
    // Every choice shown in the list's own order needs no `values` at all.
    const natural = choices.map((c) => c.data)
    patch({ values: vals.length === natural.length && vals.every((v, i) => v === natural[i]) ? undefined : vals })
  }
  const moveValue = (v: string, d: number) => {
    const i = shownValues.indexOf(v)
    const j = i + d
    if (i < 0 || j < 0 || j >= shownValues.length) return
    const next = [...shownValues]
    ;[next[i], next[j]] = [next[j], next[i]]
    setValues(next)
  }

  // ── Custom tabs ─────────────────────────────────────────────────────
  const items = tb?.items || []
  const setItems = (next: ListTabItem[]) => patch({ items: next })
  const patchItem = (i: number, p: Partial<ListTabItem>) =>
    setItems(items.map((it, k) => (k === i ? { ...it, ...p } : it)))
  const nextId = () => {
    const used = new Set(items.map((i) => i.id))
    let n = items.length + 1
    while (used.has(`tab${n}`)) n += 1
    return `tab${n}`
  }
  const states = form.list_view?.vision_states || {}
  const seedFromStates = () => {
    const seeded: ListTabItem[] = []
    for (const [state, rule] of Object.entries(states)) {
      if (!rule?.col) continue
      seeded.push({ id: state, label: RECORD_STATE_LABELS[state] || state,
        when: [{ column: rule.col, op: 'eq', value: rule.val }] })
    }
    // "Open": none of the states holds.
    const open: ListTabCondition[] = Object.values(states)
      .filter((r) => r?.col).map((r) => ({ column: r!.col, op: 'neq', value: r!.val }))
    if (open.length) seeded.unshift({ id: 'open', label: 'Open', when: open })
    startCustom(seeded.slice(0, TAB_LIMITS.maxTabs))
  }

  const tabIds = mode === 'field' ? shownValues : items.map((i) => i.id)

  return (
    <div>
      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 6 }}>
        Tabs split the list above the search scope — each tab is a condition
        the server adds to the list. People see each tab's record count; the
        last tab they used is remembered for them.
      </div>
      <div role="group" aria-label="How tabs are made" style={{ display: 'inline-flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
        {([['off', 'No tabs'], ['field', 'Split by one field'], ['custom', 'Custom tabs']] as const).map(([m, label]) => (
          <button key={m} type="button" aria-pressed={mode === m}
                  className={mode === m ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
                  onClick={() => {
                    if (m === mode) return
                    if (m === 'off') onChange(undefined)
                    else if (m === 'field') startField()
                    else startCustom()
                  }}>
            {label}
          </button>
        ))}
      </div>

      {mode === 'field' && (
        <>
          {picklistCols.length === 0 ? (
            <div style={tokenWarnStyle}>
              No field on this form has a fixed list of choices (a drop-down or
              radio field). Use Custom tabs instead.
            </div>
          ) : (
            <>
              <label style={fieldLabelStyle}>Split the list into tabs by</label>
              <select style={inputStyle} value={tb?.column || ''}
                      onChange={(e) => onChange({ ...(tb || {}), column: e.target.value, values: undefined, default: ALL_TAB_ID })}>
                {!fieldCol && <option value="">— pick a field —</option>}
                {picklistCols.map((c) => (
                  <option key={c.db_name || c.name} value={c.db_name || c.name}>
                    {c.heading || c.name}
                  </option>
                ))}
              </select>
              <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, marginTop: 8, overflow: 'hidden' }}>
                {[...shownValues, ...choices.map((c) => c.data).filter((d) => !shownValues.includes(d))].map((v, i) => {
                  const ch = choices.find((c) => c.data === v)!
                  const on = shownValues.includes(v)
                  return (
                    <div key={v} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                      borderTop: i ? '1px solid var(--color-border)' : 0 }}>
                      <input type="checkbox" checked={on} aria-label={`Show ${ch.display}`}
                             onChange={(e) => setValues(e.target.checked
                               ? [...shownValues, v] : shownValues.filter((x) => x !== v))} />
                      <span style={{ flex: 1 }}>{ch.display}</span>
                      <code style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>{v}</code>
                      {on && (
                        <>
                          <button type="button" className="btn btn-ghost btn-sm" aria-label="Move up"
                                  onClick={() => moveValue(v, -1)}><ArrowUp size={13} /></button>
                          <button type="button" className="btn btn-ghost btn-sm" aria-label="Move down"
                                  onClick={() => moveValue(v, 1)}><ArrowDown size={13} /></button>
                        </>
                      )}
                    </div>
                  )
                })}
              </div>
              {shownValues.length > TAB_LIMITS.maxTabs && (
                <div style={tokenWarnStyle}>Only the first {TAB_LIMITS.maxTabs} are shown.</div>
              )}
            </>
          )}
        </>
      )}

      {mode === 'custom' && (
        <>
          {items.map((it, i) => (
            <div key={i} style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 8, marginBottom: 8, background: 'var(--color-bg-secondary)' }}>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <input style={{ ...inputStyle, flex: 1, fontWeight: 600 }} value={it.label}
                       placeholder={`Tab ${i + 1}`} aria-label="Tab label"
                       onChange={(e) => patchItem(i, { label: e.target.value })} />
                <input style={{ ...inputStyle, width: 110 }} value={it.id} aria-label="Tab id"
                       title="The tab's id — what a person's remembered tab and links refer to"
                       onChange={(e) => patchItem(i, { id: e.target.value.replace(/\s+/g, '_').slice(0, TAB_LIMITS.maxIdLength) })} />
                <button type="button" className="btn btn-ghost btn-sm" title="Remove tab"
                        onClick={() => setItems(items.filter((_, k) => k !== i))}><Trash2 size={14} /></button>
              </div>
              {(it.when || []).map((cond, ci) => {
                const c = colOf(cond.column)
                const ops = [...filterOpsForColumn(c) as string[], 'is_empty', 'is_not_empty']
                  .filter((o) => TAB_OPS.has(o))
                const setCond = (p: Partial<ListTabCondition>) =>
                  patchItem(i, { when: it.when.map((w, k) => (k === ci ? { ...w, ...p } : w)) })
                const valueBox = (label: string, value: string | undefined, onValue: (v: string) => void) => (
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <label style={fieldLabelStyle}>{label}</label>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <input style={{ ...inputStyle, flex: 1 }} value={value || ''}
                             placeholder={acceptsCalendarDefault(c) ? 'value, :calendar_today, :var' : 'value or :variable'}
                             onChange={(e) => onValue(e.target.value)} />
                      <DefaultValuePicker hideCalendar={!acceptsCalendarDefault(c)} projectVars={projectVars}
                                          current={value || ''} onPick={onValue} extraGroups={IDENTITY_TOKENS} />
                    </div>
                  </div>
                )
                return (
                  <div key={ci} style={{ border: '1px solid var(--color-border)', borderRadius: 6, padding: 8, marginTop: 6, background: 'var(--color-bg)' }}>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <select style={{ ...inputStyle, flex: 1 }} value={cond.column}
                              onChange={(e) => {
                                const nc = colOf(e.target.value)
                                const valid = filterOpsForColumn(nc) as string[]
                                setCond({ column: e.target.value, op: valid.includes(cond.op) ? cond.op : valid[0], value: '', from: undefined, to: undefined })
                              }}>
                        {mainCols.map((col) => (
                          <option key={col.db_name || col.name} value={col.db_name || col.name}>{col.heading || col.name}</option>
                        ))}
                      </select>
                      <select style={{ ...inputStyle, width: 120 }} value={cond.op}
                              onChange={(e) => setCond({ op: e.target.value as EnforcedFilterOp })}>
                        {ops.map((o) => <option key={o} value={o}>{OP_LABELS[o] || o}</option>)}
                      </select>
                      <button type="button" className="btn btn-ghost btn-sm" title="Remove condition"
                              disabled={it.when.length === 1}
                              onClick={() => patchItem(i, { when: it.when.filter((_, k) => k !== ci) })}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                    {cond.op === 'between' ? (
                      <div style={{ display: 'flex', gap: 6 }}>
                        {valueBox('From', cond.from, (v) => setCond({ from: v }))}
                        {valueBox('To', cond.to, (v) => setCond({ to: v }))}
                      </div>
                    ) : cond.op === 'is_empty' || cond.op === 'is_not_empty' ? null : (
                      valueBox(cond.op === 'in' ? 'Values (comma-separated)' : 'Value',
                        Array.isArray(cond.value) ? cond.value.join(', ') : cond.value,
                        (v) => setCond({ value: v }))
                    )}
                  </div>
                )
              })}
              {it.when.length < TAB_LIMITS.maxConditionsPerTab && (
                <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 6 }}
                        onClick={() => {
                          const first = mainCols[0]
                          patchItem(i, { when: [...it.when, { column: first ? (first.db_name || first.name) : '', op: 'eq', value: '' }] })
                        }}>
                  <Plus size={13} /> And another condition
                </button>
              )}
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {items.length < TAB_LIMITS.maxTabs && (
              <button type="button" className="btn btn-secondary btn-sm"
                      onClick={() => {
                        const first = mainCols[0]
                        const id = nextId()
                        setItems([...items, { id, label: `Tab ${items.length + 1}`,
                          when: [{ column: first ? (first.db_name || first.name) : '', op: 'eq', value: '' }] }])
                      }}>
                <Plus size={13} /> Add tab
              </button>
            )}
            {Object.values(states).some((r) => r?.col) && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={seedFromStates}
                      title="Make Open / Confirmed / Cancelled / Closed tabs from this screen's record states">
                Start from record states
              </button>
            )}
          </div>
        </>
      )}

      {mode !== 'off' && (
        <>
          <label style={checkboxRowStyle}>
            <input type="checkbox" checked={tb?.show_all !== false}
                   onChange={(e) => patch({ show_all: e.target.checked })} />
            <span style={{ fontSize: '0.8825rem' }}>Show an “All” tab</span>
          </label>
          <label style={checkboxRowStyle}>
            <input type="checkbox" checked={tb?.counts !== false}
                   onChange={(e) => patch({ counts: e.target.checked })} />
            <span style={{ fontSize: '0.8825rem' }}>Show the number of records on each tab</span>
          </label>
          <label style={fieldLabelStyle}>Tab opened first</label>
          <select style={inputStyle} value={tb?.default || ALL_TAB_ID}
                  onChange={(e) => patch({ default: e.target.value })}>
            {tb?.show_all !== false && <option value={ALL_TAB_ID}>All</option>}
            {tabIds.map((id) => {
              const label = mode === 'field' ? (choices.find((c) => c.data === id)?.display || id)
                : (items.find((x) => x.id === id)?.label || id)
              return <option key={id} value={id}>{label}</option>
            })}
          </select>
          {!effective && (
            <div style={tokenWarnStyle}>
              No usable tab yet — complete a tab’s conditions (every condition needs
              a field, an operator and a value) or pick a field with choices.
            </div>
          )}
          {effective && mode === 'custom' && effective.items.length < items.length && (
            <div style={tokenWarnStyle}>
              {items.length - effective.items.length} tab(s) are not shown: a condition
              is incomplete, an id is repeated or blank, or there are too many conditions.
            </div>
          )}
        </>
      )}
    </div>
  )
}
